/**
 * JSON工具类 - 智能解析和格式化
 * 兼容各种非标准JSON格式，同时保持原始格式不变
 */

/**
 * 修复非标准JSON字符串
 * 处理以下问题：
 * 1. 全角标点符号（：，；""''｛｝【】） → 半角
 * 2. 控制字符（\x00-\x08, \x0B, \x0C, \x0E-\x1F, \x7F）
 * 3. 中文引号（"" ''） → 英文引号
 * 4. 多余的空白字符
 * 
 * @param {string} jsonStr - 可能包含非法字符的JSON字符串
 * @returns {string} - 修复后的合法JSON字符串
 */
export function fixInvalidJSON(jsonStr) {
  if (typeof jsonStr !== 'string') {
    return jsonStr;
  }

  let fixed = jsonStr;

  // 1. 替换全角标点符号为半角
  fixed = fixed
    .replace(/：/g, ':')      // 全角冒号 → 半角
    .replace(/，/g, ',')      // 全角逗号 → 半角
    .replace(/；/g, ';')      // 全角分号 → 半角
    .replace(/｛/g, '{')      // 全角左花括号 → 半角
    .replace(/｝/g, '}')      // 全角右花括号 → 半角
    .replace(/【/g, '[')      // 全角左方括号 → 半角
    .replace(/】/g, ']')      // 全角右方括号 → 半角
    .replace(/（/g, '(')      // 全角左圆括号 → 半角
    .replace(/）/g, ')');     // 全角右圆括号 → 半角

  // 2. 替换中文引号为英文引号
  fixed = fixed
    .replace(/"/g, '"')       // 左双引号 → "
    .replace(/"/g, '"')       // 右双引号 → "
    .replace(/'/g, "'")       // 左单引号 → '
    .replace(/'/g, "'");      // 右单引号 → '

  // 3. 移除非法控制字符（保留合法的 \t \n \r）
  fixed = fixed
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .replace(/\r\n/g, '\n')   // 统一换行符
    .replace(/\r/g, '\n');

  return fixed;
}

/**
 * 智能解析JSON
 * 自动修复常见格式问题后解析
 * 
 * @param {string} jsonStr - JSON字符串
 * @param {boolean} tryFix - 是否尝试修复（默认true）
 * @returns {object} - 解析后的对象
 * @throws {Error} - 解析失败时抛出异常
 */
export function smartParseJSON(jsonStr, tryFix = true) {
  if (typeof jsonStr !== 'string') {
    return jsonStr;
  }

  const trimmed = jsonStr.trim();
  if (!trimmed) {
    return null;
  }

  // 第一次尝试：直接解析
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    if (!tryFix) {
      throw e;
    }
  }

  // 第二次尝试：修复后解析
  try {
    const fixed = fixInvalidJSON(trimmed);
    console.log('JSON修复前:', jsonStr.substring(0, 100));
    console.log('JSON修复后:', fixed.substring(0, 100));
    return JSON.parse(fixed);
  } catch (e) {
    throw new Error(`JSON解析失败: ${e.message}\n原始内容: ${jsonStr.substring(0, 200)}`);
  }
}

/**
 * 安全解析JSON
 * 解析失败时返回默认值，不抛出异常
 * 
 * @param {string} jsonStr - JSON字符串
 * @param {*} defaultValue - 解析失败时的默认值
 * @param {boolean} tryFix - 是否尝试修复
 * @returns {*} - 解析后的对象或默认值
 */
export function safeParseJSON(jsonStr, defaultValue = null, tryFix = true) {
  try {
    return smartParseJSON(jsonStr, tryFix);
  } catch (e) {
    console.warn('JSON解析失败，使用默认值:', e.message);
    return defaultValue;
  }
}

/**
 * 检测JSON字符串的格式类型
 * 
 * @param {string} jsonStr - JSON字符串
 * @returns {string} - 格式类型：'object' | 'array' | 'invalid'
 */
export function detectJSONFormat(jsonStr) {
  if (typeof jsonStr !== 'string') {
    return 'invalid';
  }

  const trimmed = jsonStr.trim();
  if (!trimmed) {
    return 'invalid';
  }

  if (trimmed.startsWith('{')) {
    return 'object';
  } else if (trimmed.startsWith('[')) {
    return 'array';
  }

  return 'invalid';
}

/**
 * 将对象格式化为JSON字符串
 * 保持格式一致性
 * 
 * @param {object} obj - 要格式化的对象
 * @param {number} space - 缩进空格数（默认2）
 * @returns {string} - JSON字符串
 */
export function formatToJSON(obj, space = 2) {
  if (obj === null || obj === undefined) {
    return '';
  }

  try {
    return JSON.stringify(obj, null, space);
  } catch (e) {
    console.error('JSON序列化失败:', e);
    return '';
  }
}

/**
 * 深拷贝对象（通过JSON序列化）
 * 
 * @param {object} obj - 要拷贝的对象
 * @returns {object} - 拷贝后的对象
 */
export function deepClone(obj) {
  if (obj === null || obj === undefined) {
    return obj;
  }

  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (e) {
    console.warn('深拷贝失败，返回原始对象:', e);
    return obj;
  }
}

/**
 * 标准化期望答案数据
 * 兼容多种格式并统一为内部格式
 * 
 * @param {object|string} data - 期望答案数据
 * @returns {object} - 标准化后的数据
 */
export function normalizeExpectedAnswer(data) {
  if (!data) {
    return {};
  }

  // 如果是字符串，先解析
  let parsed = data;
  if (typeof data === 'string') {
    parsed = safeParseJSON(data, null);
    if (!parsed) {
      return {};
    }
  }

  // 格式1: 非数组对象，值为数组 {"发票号码":["123"]}
  // 格式2: 数组包对象 [{"发票号码":"123"}]
  // 格式3: 非数组对象，值为字符串 {"发票号码":"123"}
  // 格式4: 嵌套对象 {"基础统计信息": {"图片数量": 6}}
  
  return parsed;
}
